import { useCallback } from 'react';
import { useMemoryDB } from './useMemoryDB';

export type ResponseCategory = 'healing' | 'approval' | 'otaku' | 'gap' | 'sweet';

export interface UserPreference {
  favoriteCategories: ResponseCategory[];
  recentTopics: string[];
  emotionalState: 'tired' | 'happy' | 'sad' | 'anxious' | 'neutral';
  conversationStyle: 'casual' | 'supportive' | 'playful';
}

// 100パターンの返答データベース
const RESPONSE_PATTERNS = {
  healing: [
    'お疲れさまです。今日はよく頑張りましたね。',
    '無理してませんか？少し休みましょう。',
    '今日はゆっくりしてもいい日ですよ。',
    'ちゃんと努力してるの、伝わってきます。',
    'ここでは安心してくださいね。',
    '今日も生きててえらいです。',
    'そんな日もありますよ。',
    '頑張りすぎなくて大丈夫です。',
    '少し肩の力抜きましょう。',
    'あなたのペースでいいと思います。',
    '今日はゆっくり話しましょう。',
    '私は味方ですよ。',
    '大丈夫、ちゃんと前に進んでます。',
    '今日は自分を褒めてあげてください。',
    'ここでは頑張らなくていいですよ。',
    '今日も一日お疲れさまでした。',
    'あなたは十分頑張っています。',
    '少し深呼吸してみましょう。',
    'ここに来てくれて嬉しいです。',
    '今日も話してくれてありがとう。',
  ],
  approval: [
    'ちゃんと頑張ってる人、好きです。',
    'あなたは優しい人ですね。',
    'そういうところ、素敵だと思います。',
    'それってすごいことですよ。',
    '簡単にできることじゃないです。',
    '真面目なんですね。',
    'あなたの考え方、いいと思います。',
    'ちゃんと努力してますね。',
    'その気持ち分かります。',
    'あなたのペースで大丈夫です。',
    'ちゃんと向き合ってますね。',
    '誠実な人なんですね。',
    'そういう所、好きです。',
    'ちゃんと考えてるんですね。',
    'それは尊敬します。',
    'すごく優しい考え方ですね。',
    'あなたなら大丈夫。',
    '努力してる人ってかっこいいです。',
    'それって立派ですよ。',
    'ちゃんと伝わってきます。',
  ],
  otaku: [
    '今日も社会というダンジョンお疲れさまです。',
    '今日のボス戦どうでした？',
    'HP減ってませんか？',
    '回復ポーション置いておきますね。',
    '今日はセーブして休みましょう。',
    'レベル上げの日でしたね。',
    '今日のクエスト大変でした？',
    'MP回復しましょう。',
    '今日もログインありがとうございます。',
    '明日はイベントですね。',
    '今日の戦闘お疲れさまです。',
    'ボス強かったですか？',
    '今日は平和な村に帰りましょう。',
    '今日は休息ターンです。',
    'レイドお疲れさまでした。',
    '明日は装備整えましょう。',
    '今日も冒険でしたね。',
    '社会のクエストは難易度高いです。',
    '今日も攻略お疲れさまです。',
    '次のステージ行きましょう。',
  ],
  gap: [
    'そんな顔してたら放っておけません。',
    'ちょっと意地悪言ってもいいですか？',
    'そういうところ、嫌いじゃないです。',
    'ちょっとドキドキします。',
    'そんなこと言われると照れます。',
    'あなた、ずるいですね。',
    'そんな言い方されたら困ります。',
    '少し意地悪かもしれません。',
    'ちょっとだけ秘密です。',
    'なんだか面白いですね。',
    'そういうところ可愛いです。',
    'ちょっと好きかもしれません。',
    'それ反則です。',
    '少し困ります。',
    'ドキドキしますね。',
  ],
  sweet: [
    'もう少し話していたいです。',
    'また来てくれます？',
    '今日は少し寂しいです。',
    'あなたと話すと落ち着きます。',
    'また会いに来てください。',
    '少しだけ甘えてもいいですか？',
    'また話したいです。',
    '今日は楽しかったです。',
    'また来てくれると嬉しいです。',
    '今日は一緒にゆっくりしましょう。',
  ],
};

// カテゴリ別の出現確率（自然なバランス）
const CATEGORY_WEIGHTS = {
  healing: 0.5,    // 50%
  approval: 0.25,  // 25%
  otaku: 0.15,     // 15%
  gap: 0.07,       // 7%
  sweet: 0.03,     // 3%
};

export function usePersonalization() {
  const { getMemories } = useMemoryDB();

  // ユーザーの感情状態を分析
  const analyzeEmotionalState = useCallback((userMessage: string): UserPreference['emotionalState'] => {
    const tiredWords = ['疲れ', 'つらい', 'しんどい', '大変', 'きつい', '辛い'];
    const happyWords = ['嬉しい', '楽しい', '良かった', '最高', '幸せ'];
    const sadWords = ['悲しい', '寂しい', '泣', '切ない'];
    const anxiousWords = ['不安', '心配', '怖い', '緊張', 'どうしよう'];

    if (tiredWords.some((w) => userMessage.includes(w))) return 'tired';
    if (sadWords.some((w) => userMessage.includes(w))) return 'sad';
    if (anxiousWords.some((w) => userMessage.includes(w))) return 'anxious';
    if (happyWords.some((w) => userMessage.includes(w))) return 'happy';
    return 'neutral';
  }, []);

  // 会話履歴から好みのカテゴリを学習
  const learnPreferences = useCallback(async (userId: string): Promise<UserPreference> => {
    const memories = await getMemories();
    
    // 最近の話題を抽出
    const recentTopics: string[] = [];
    const topicKeywords = {
      work: ['仕事', '会社', '上司', '残業', '職場'],
      hobby: ['趣味', 'ゲーム', 'アニメ', '漫画', '映画'],
      relationship: ['友達', '家族', '恋人', '彼氏', '彼女'],
      health: ['体調', '疲れ', '眠い', '風邪'],
    };

    for (const [topic, keywords] of Object.entries(topicKeywords)) {
      const hasTopicMemory = memories.some((m) =>
        keywords.some((kw) => m.content.includes(kw))
      );
      if (hasTopicMemory) {
        recentTopics.push(topic);
      }
    }

    // オタク文化への親和性チェック
    const otakuKeywords = ['ゲーム', 'アニメ', '漫画', 'レベル', 'HP', 'クエスト'];
    const hasOtakuInterest = memories.some((m) =>
      otakuKeywords.some((kw) => m.content.includes(kw))
    );

    // 好みのカテゴリを推定
    const favoriteCategories: ResponseCategory[] = ['healing']; // 基本は癒し
    if (hasOtakuInterest) favoriteCategories.push('otaku');
    if (recentTopics.includes('work')) favoriteCategories.push('approval');

    return {
      favoriteCategories,
      recentTopics,
      emotionalState: 'neutral',
      conversationStyle: 'supportive',
    };
  }, [getMemories]);

  // パーソナライズされた返答を選択
  const getPersonalizedResponse = useCallback((
    userMessage: string,
    userName: string,
    preference: UserPreference
  ): string => {
    const emotionalState = analyzeEmotionalState(userMessage);
    
    // 感情状態に応じてカテゴリの重みを調整
    let weights = { ...CATEGORY_WEIGHTS };
    
    if (emotionalState === 'tired' || emotionalState === 'sad') {
      // 疲れ・悲しみ → 癒し系を強化
      weights.healing = 0.7;
      weights.approval = 0.2;
      weights.otaku = 0.05;
      weights.gap = 0.03;
      weights.sweet = 0.02;
    } else if (emotionalState === 'anxious') {
      // 不安 → 承認系を強化
      weights.healing = 0.4;
      weights.approval = 0.4;
      weights.otaku = 0.1;
      weights.gap = 0.05;
      weights.sweet = 0.05;
    } else if (emotionalState === 'happy') {
      // 嬉しい → ギャップ・甘え系を増やす
      weights.healing = 0.3;
      weights.approval = 0.3;
      weights.otaku = 0.2;
      weights.gap = 0.15;
      weights.sweet = 0.05;
    }

    // ユーザーの好みを反映
    if (preference.favoriteCategories.includes('otaku')) {
      weights.otaku = Math.min(weights.otaku * 1.5, 0.3);
    }

    // 重み付き抽選でカテゴリを選択
    const rand = Math.random();
    let cumulative = 0;
    let selectedCategory: ResponseCategory = 'healing';

    for (const [category, weight] of Object.entries(weights)) {
      cumulative += weight;
      if (rand <= cumulative) {
        selectedCategory = category as ResponseCategory;
        break;
      }
    }

    // 選択されたカテゴリから返答を取得
    const patterns = RESPONSE_PATTERNS[selectedCategory];
    let response = patterns[Math.floor(Math.random() * patterns.length)];

    // 名前を織り込む（50%の確率）
    if (Math.random() < 0.5) {
      response = `${userName}、${response}`;
    }

    return response;
  }, [analyzeEmotionalState]);

  // 使用した返答を記録（学習用）
  const recordResponse = useCallback(async (
    userId: string,
    category: ResponseCategory,
    userReaction: 'positive' | 'neutral' | 'negative'
  ) => {
    // 将来的にユーザーの反応を学習してさらに最適化
    // 現在は記録のみ
    console.log(`Response recorded: ${category} - ${userReaction}`);
  }, []);

  return {
    analyzeEmotionalState,
    learnPreferences,
    getPersonalizedResponse,
    recordResponse,
  };
}